import { cn } from "../../../lib/utils";
import type { CoverageBarColor } from "../lpConfig";
import { lpConfig } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

const COVERAGE_BAR_COLORS: Record<
  CoverageBarColor,
  { filled: string; bg: string }
> = {
  green: { filled: "bg-[#30BF78]", bg: "bg-[#30BF78]/10" },
  orange: { filled: "bg-[#ff8d00]", bg: "bg-[#ff8d00]/10" },
  blue: { filled: "bg-[#3B82F6]", bg: "bg-[#3B82F6]/10" },
  red: { filled: "bg-red-400", bg: "bg-red-400/10" },
};

/**
 * パイプラインカバレッジ図を描画する
 * @return {JSX.Element} カバレッジ図
 */
function PipelineCoverage() {
  const { pipelineCoverage, pipelineSteps } = lpConfig.comparisonTable;

  return (
    <div className="mb-12 p-6 sm:p-8 rounded-2xl bg-gray-50 border border-gray-100">
      {/* Pipeline steps row */}
      <div className="flex items-center justify-between mb-8 overflow-x-auto gap-1">
        {pipelineSteps.map((step, i) => (
          <div key={step} className="flex items-center shrink-0">
            <div className="flex flex-col items-center">
              <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#02072F] text-white flex items-center justify-center text-xs sm:text-sm font-bold">
                {i + 1}
              </span>
              <span className="text-[10px] sm:text-xs text-gray-600 mt-1 whitespace-nowrap">
                {step}
              </span>
            </div>
            {i < pipelineSteps.length - 1 && (
              <div className="w-6 sm:w-10 h-0.5 bg-gray-300 mx-1 sm:mx-2 mt-[-12px]" />
            )}
          </div>
        ))}
      </div>

      {/* Coverage bars */}
      <div className="space-y-3">
        {pipelineCoverage.map(item => {
          const colors =
            COVERAGE_BAR_COLORS[item.color] ?? COVERAGE_BAR_COLORS.green;
          return (
            <div key={item.name} className="flex items-center gap-3 sm:gap-4">
              <span className="w-24 sm:w-28 text-xs sm:text-sm font-medium text-gray-700 shrink-0 text-right">
                {item.name}
              </span>
              <div
                className={cn(
                  "flex-1 h-6 sm:h-7 rounded-full overflow-hidden",
                  colors.bg,
                )}
              >
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-700",
                    colors.filled,
                  )}
                  style={{ width: `${Math.max(item.percent, 2)}%` }}
                />
              </div>
              <span className="text-xs sm:text-sm font-bold text-gray-800 w-16 sm:w-20 shrink-0">
                {item.percent}%
                {item.note ? (
                  <span className="text-[10px] text-gray-400 ml-1">
                    {item.note}
                  </span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const LEGACY_TO_SYMBOL: Record<string, string> = {
  excellent: "◎",
  good: "△",
  poor: "×",
};

/**
 * 評価記号をパースして色分け表示する
 * ◎ → 緑、○ → 青、△ → オレンジ、× → 赤
 * @param {string} rating - 評価値
 * @param {boolean} isStrix - StrixSearchカラムかどうか
 * @return {JSX.Element} 評価表示要素
 */
function RatingCell({ rating, isStrix }: { rating: string; isStrix: boolean }) {
  // Normalize legacy tokens ("excellent" → "◎", etc.) then handle uniformly
  const normalized = LEGACY_TO_SYMBOL[rating] ?? rating;

  // Symbol-prefixed ratings: "◎ AI自動仕分け", "○ 根拠可視化", "△ 連携", "× なし"
  const symbolMatch = normalized.match(/^([◎○△×])\s*(.*)/);
  if (symbolMatch) {
    const [, symbol, text] = symbolMatch;
    const colorClass =
      symbol === "◎"
        ? "text-[#30BF78] font-bold"
        : symbol === "○"
          ? "text-[#3B82F6] font-semibold"
          : symbol === "△"
            ? "text-[#ff8d00]"
            : "text-red-400";
    return (
      <span className={colorClass}>
        {symbol}
        {text ? <span className="text-xs ml-0.5">{text}</span> : null}
      </span>
    );
  }

  // Dash / unknown
  if (normalized === "-" || normalized === "不明") {
    return <span className="text-gray-300">{normalized}</span>;
  }

  // Plain text (prices, percentages, etc.)
  return (
    <span
      className={
        isStrix ? "text-white font-bold text-sm" : "text-gray-700 text-sm"
      }
    >
      {normalized}
    </span>
  );
}

/**
 * 他ツールとの機能比較テーブルセクション
 * カテゴリヘッダ行、パイプラインカバレッジ図、脚注、6列対応
 * @return {JSX.Element} 比較テーブルセクションコンポーネント
 */
export function LpComparisonTable() {
  const { ref, isVisible } = useFadeIn();

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div
        ref={ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest text-[#ff8d00] uppercase mb-4">
            {lpConfig.comparisonTable.sectionLabel}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F1649] mb-4">
            {lpConfig.comparisonTable.heading}
          </h2>
          <p className="text-gray-500 mb-12">
            {lpConfig.comparisonTable.subtitle}
          </p>
        </div>

        {/* Pipeline Coverage Chart */}
        <PipelineCoverage />

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] rounded-2xl overflow-hidden border border-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-4 px-4 text-left text-sm font-medium text-gray-500 w-36">
                  機能
                </th>
                {lpConfig.comparisonTable.competitors.map(
                  (competitor, index) =>
                    index === 0 ? (
                      <th
                        key={competitor}
                        className="py-4 px-3 text-center bg-[#02072F] text-white font-bold"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className="inline-block px-2 py-0.5 text-[10px] rounded-full bg-[#ff8d00] text-white font-bold">
                            おすすめ
                          </span>
                          {competitor}
                        </div>
                      </th>
                    ) : (
                      <th
                        key={competitor}
                        className="py-4 px-3 text-center text-sm font-medium text-gray-500"
                      >
                        {competitor}
                      </th>
                    ),
                )}
              </tr>
            </thead>
            <tbody>
              {lpConfig.comparisonTable.rows.map((row, index) => {
                // Category header row
                if (row.isCategory) {
                  return (
                    <tr key={row.feature} className="bg-gray-100">
                      <td
                        colSpan={
                          lpConfig.comparisonTable.competitors.length + 1
                        }
                        className="py-3 px-4 text-sm font-bold text-[#0F1649] tracking-wide"
                      >
                        {row.feature}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={row.feature}
                    className={cn(
                      "border-t border-gray-200",
                      index % 2 === 1 && "bg-gray-50/50",
                    )}
                  >
                    <td className="py-3 px-4 text-sm font-medium text-gray-700">
                      {row.feature}
                    </td>
                    {row.ratings.map((rating, ratingIndex) => (
                      <td
                        key={`${row.feature}-${ratingIndex}`}
                        className={cn(
                          "py-3 px-3 text-center",
                          ratingIndex === 0 && "bg-[#02072F]/5",
                        )}
                      >
                        <RatingCell
                          rating={rating}
                          isStrix={ratingIndex === 0}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footnote */}
        {lpConfig.comparisonTable.footnote && (
          <p className="mt-4 text-xs text-gray-400 leading-relaxed">
            {lpConfig.comparisonTable.footnote}
          </p>
        )}
      </div>
    </section>
  );
}
